// Bookie · Za'faron — preview thumbnail. The full design lives at
// /templates/bookie-saffron.html (Navro'z holiday family gathering,
// saffron #D88A2A on cream paper, Yeseva One display + Cormorant
// Garamond italic body + a Pinyon Script Navruz blessing, Uzbek
// paisley (botte / buta) in the hero corners). The catalog card is
// a compact 5:7 echo of that hero: a single paisley teardrop up top,
// the saffron eyebrow, the Pinyon-script "Navro'z muborak" blessing,
// a thin gold rule, then the host family name. Iframe-free because
// it has to render at any pixel size inside the catalog grid.

import type { LayoutCtx } from "./_shared";

export function BookieSaffronPreviewLayout({ names, date, venue, ink, bg, font }: LayoutCtx) {
  // For this template "names" is a single host family name (e.g.
  // "Karimov oilasi"), not a couple. If a stray "&" leaks in from a
  // wedding-shaped catalog row, just collapse to the first part so
  // the thumbnail still reads cleanly.
  const family = names.split(/\s*&\s*/)[0] || names;
  // Hardcode the brand accents so the catalog card always reads
  // "saffron + cream + small deep-gold" regardless of which palette
  // the catalog row was tagged with upstream.
  const saffron = "#D88A2A";
  const saffronDeep = "#A85F1B";
  const saffronSoft = "#E8B36A";
  const paper = "#FBF3E4";
  const paper2 = "#F4E7CC";
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: `linear-gradient(180deg, ${paper} 0%, ${bg} 22%, ${paper2} 100%)`,
        color: ink,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        padding: "13% 9% 9%",
      }}
    >
      {/* Uzbek paisley (botte / buta) — single teardrop with internal
          scrollwork, the signature motif of this template. */}
      <svg
        aria-hidden
        viewBox="0 0 60 76"
        style={{ width: "22%", height: "auto", color: saffron, opacity: 0.95 }}
      >
        <path
          d="M30 3 C 16 5 6 18 6 38 C 6 56 18 70 32 70 C 46 70 54 60 54 48 C 54 38 46 32 38 32 C 30 32 26 38 26 44 C 26 50 30 54 36 54"
          stroke="currentColor"
          strokeWidth="0.9"
          fill="none"
        />
        <path
          d="M30 11 C 18 13 12 24 12 38 C 12 52 22 62 32 62 C 42 62 48 54 48 46"
          stroke={saffronDeep}
          strokeWidth="0.5"
          fill="none"
          opacity={0.7}
        />
        <ellipse cx="20" cy="28" rx="3" ry="1.5" transform="rotate(-26 20 28)" fill={saffronSoft} opacity={0.85} />
        <ellipse cx="26" cy="18" rx="2.6" ry="1.3" transform="rotate(-12 26 18)" fill={saffronSoft} opacity={0.9} />
        <circle cx="32" cy="46" r="1.8" fill={saffronDeep} />
        <circle cx="32" cy="46" r="0.7" fill={paper} />
      </svg>

      {/* Saffron eyebrow — Navruz-specific */}
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 7,
          letterSpacing: "0.45em",
          textTransform: "uppercase",
          color: saffronDeep,
          marginTop: 12,
          fontWeight: 700,
        }}
      >
        BAHOR &middot; 21 MART
      </div>

      {/* Pinyon Script blessing — the cultural anchor */}
      <div
        style={{
          fontFamily: "'Pinyon Script', cursive",
          fontSize: 24,
          color: saffron,
          lineHeight: 1.05,
          marginTop: 8,
        }}
      >
        Navro&apos;z muborak
      </div>

      {/* Display headline — the occasion */}
      <div
        style={{
          fontFamily: "'Yeseva One', serif",
          fontWeight: 400,
          fontSize: 20,
          lineHeight: 1.02,
          color: ink,
          marginTop: 6,
          maxWidth: "100%",
        }}
      >
        Navro&apos;z bayrami
      </div>

      {/* Gold rule */}
      <div
        aria-hidden
        style={{
          width: 28,
          height: 1,
          background: saffron,
          opacity: 0.9,
          margin: "10px 0 6px",
        }}
      />

      {/* Italic sub-tagline */}
      <div
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontStyle: "italic",
          fontSize: 13,
          color: ink,
          opacity: 0.85,
          lineHeight: 1.25,
        }}
      >
        oilaviy dasturxon
      </div>

      {/* Host family (single name, not a couple) */}
      <div
        style={{
          marginTop: 14,
          fontFamily: "'Yeseva One', serif",
          fontWeight: 400,
          fontSize: 15,
          color: saffronDeep,
          lineHeight: 1.2,
          letterSpacing: "0.03em",
        }}
      >
        {family}
      </div>

      {/* Date + venue (footer) */}
      <div
        style={{
          marginTop: "auto",
          paddingTop: 12,
          fontFamily: "var(--font-ui)",
          fontSize: 7.5,
          letterSpacing: "0.32em",
          color: ink,
          opacity: 0.78,
          fontFeatureSettings: '"tnum" 1, "lnum" 1',
        }}
      >
        {date.replace(/\./g, " · ")}
      </div>
      <div
        style={{
          fontFamily: font,
          fontStyle: "italic",
          fontSize: 10,
          color: ink,
          opacity: 0.7,
          marginTop: 2,
        }}
      >
        {venue}
      </div>
    </div>
  );
}
